import { describeIssue, type CompletenessSummary } from '@motorcity/shared';

/**
 * Publish-readiness for the record on screen.
 *
 * Never blocks saving — an editor must be able to park a half-finished draft.
 * It states plainly what is still outstanding and what the consequence is, so
 * nothing is discovered only after it reaches the app.
 */
export function ReadinessPanel({
  summary,
  status,
  entity,
}: {
  summary: CompletenessSummary;
  status: 'draft' | 'live';
  entity: string;
}) {
  const { required, recommended, readyToPublish } = summary;

  if (readyToPublish && recommended.length === 0) {
    return (
      <div className="readiness ok" role="status">
        <Icon kind="ok" />
        <div>
          <strong>Ready to publish</strong>
          <p>Every required field on this {entity} is filled in.</p>
        </div>
      </div>
    );
  }

  // A live record with gaps is already reaching customers, so it is stated as a
  // problem rather than as advice.
  const kind = required.length > 0 ? (status === 'live' ? 'error' : 'warn') : 'info';

  return (
    <div className={`readiness ${kind}`} role={kind === 'error' ? 'alert' : 'status'}>
      <Icon kind={kind} />
      <div>
        <strong>
          {required.length > 0
            ? status === 'live'
              ? `This ${entity} is Live but incomplete`
              : `${required.length} required field${required.length === 1 ? '' : 's'} still missing`
            : 'Complete, with some suggestions'}
        </strong>

        {required.length > 0 ? (
          <>
            <p>
              {status === 'live'
                ? 'It is already published to the app with these gaps. Fill them in or set it back to Draft.'
                : `You can keep saving as a draft, but this ${entity} should not be set Live until these are filled in.`}
            </p>
            <ul className="readiness-list">
              {required.map((issue, i) => (
                <li key={`${issue.field}-${issue.locale ?? ''}-${i}`}>{describeIssue(issue)}</li>
              ))}
            </ul>
          </>
        ) : null}

        {recommended.length > 0 ? (
          <details className="readiness-more">
            <summary>
              {recommended.length} recommended{required.length > 0 ? ' as well' : ''}
            </summary>
            <ul className="readiness-list">
              {recommended.map((issue, i) => (
                <li key={`${issue.field}-${issue.locale ?? ''}-${i}`}>{describeIssue(issue)}</li>
              ))}
            </ul>
          </details>
        ) : null}
      </div>
    </div>
  );
}

/** Inline warning beside the Draft/Live control, shown only when Live is selected. */
export function LiveWarning({ summary }: { summary: CompletenessSummary }) {
  if (summary.readyToPublish) return null;
  return (
    <p className="field-error" role="alert">
      {summary.required.length} required field
      {summary.required.length === 1 ? '' : 's'} still missing — this will publish incomplete.
    </p>
  );
}

function Icon({ kind }: { kind: 'ok' | 'warn' | 'error' | 'info' }) {
  const path =
    kind === 'ok'
      ? 'M20 6 9 17l-5-5'
      : kind === 'info'
        ? 'M12 16v-4m0-4h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z'
        : 'M12 8v5m0 3.5h.01M10.3 3.9 1.9 18a2 2 0 0 0 1.7 3h16.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z';
  return (
    <svg
      className="readiness-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={path} />
    </svg>
  );
}
